import {
  createBaseThemeOptions,
  createUnifiedTheme,
  PageTheme,
  pageTheme as defaultPageThemes,
  palettes,
} from '@backstage/theme';
import { alpha } from '@material-ui/core';

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).map(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#092241',
  };
});

export const dlrTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      DataGrid: {
        bg: '#fff',
        pinnedBg: '#fff',
        headerBg: '#fff',
      },
      primary: {
        main: '#1456E0',
        light: '#D0DEE5',
        dark: '#071454',
      },
      error: {
        main: '#DB0028',
        light: '#EBD7E0',
        dark: '#E11D48',
      },
      success: {
        main: '#4CAF50',
        light: '#D0DEE5',
        dark: '#0CA678',
      },
      warning: {
        main: '#FFA000',
        light: '#E8E3DF',
        dark: '#EE9B00',
      },
      background: {
        default: '#F5F7FE',
        paper: '#FFF',
      },
      lightBlue: '#F5F7FF',
      text: {
        primary: '#092241',
        secondary: 'rgba(9, 34, 65, 0.7)',
        tertiary: 'rgba(9, 34, 65, 0.5)',
      },
      navigation: {
        background: 'linear-gradient(0deg, #0D2497 0%, #1456E0 100%)',
        indicator: '#2684FF',
        color: '#A1BCF3',
        selectedColor: '#ffffff',
        navItem: {
          hoverBackground: 'rgba(255,255,255, .16)',
        },
      },
    },
  }),
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  fontFamily: 'Open Sans',
  components: {
    BackstageSidebarItem: {
      styleOverrides: {
        label: {
          fontFamily: 'Red Hat Display',
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: ({ theme }) => ({
          backgroundImage: 'unset',
          boxShadow: 'unset',
          paddingBottom: theme.spacing(1),
        }),
        title: ({ theme }) => ({
          color: theme.page.fontColor,
          fontWeight: 700,
          fontFamily: 'Red Hat Display',
          outline: 'none',
        }),
        subtitle: ({ theme }) => ({
          color: alpha(theme.page.fontColor, 0.8),
          fontSize: 14,
          maxWidth: 'none',
        }),
        type: ({ theme }) => ({
          color: alpha(theme.page.fontColor, 0.8),
          fontSize: 12,
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          fontFamily: 'Red Hat Display',
          fontSize: 20,
        },
      },
    },
    BackstageFavoriteToggleIcon: {
      styleOverrides: {
        icon: {
          color: '#1456E0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {},
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&[aria-expanded=true]': {},
          '&[aria-expanded=true] path': {
            fill: '#FFFFFF',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontFamily: 'Red Hat Display',
          fontSize: 20,
          fontWeight: 700,
          lineHeight: '24px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '& [class*=MuiPaper-rounded]': {
            border: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0 12px 20px -12px rgba(0,0,0,.12)',
          borderColor: '#E1E4E8',
          borderWidth: 1,
          borderStyle: 'solid',
          borderRadius: 8,
        },
        elevation2: {
          boxShadow: '0 12px 20px -12px rgba(0,0,0,.12)',
          borderColor: '#E1E4E8',
          borderWidth: 1,
          borderStyle: 'solid',
          borderRadius: 8,
        },
        root: ({ theme }) => ({
          color: theme.page.fontColor,
          '&[class*=searchBarContainer]': {
            border: 'none',
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: '#5585ff !important',
            textDecoration: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        sizeSmall: {
          padding: '12px 16px 12px 20px !important',
        },
        body: {
          paddingLeft: 20,
        },
        paddingCheckbox: {
          '&:last-child': {
            paddingRight: 20,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:not([index], &:first-of-type, [class^=MuiTableRow-footer])': {
            height: '0 !important',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '& [data-testid="edit-announcement"]': {
            padding: '6px 16px',
            borderRadius: '0',
          },
        },
      },
    },
    BackstageErrorPage: {
      styleOverrides: {
        title: {
          fontFamily: 'Red Hat Display',
        },
        subtitle: {
          marginBottom: 16,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabRoot: {
          fontFamily: 'Red Hat Display',
          fontWeight: 700,
          fontSize: 14,
          padding: '12px 16px',
        },
        selected: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
        tabsWrapper: { backgroundColor: 'transparent' },
      },
    },
    // @ts-ignore
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: 'rgba(9, 34, 65, 0.12)',
        },
        groupWrapper: {
          borderRadius: 4,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(9, 34, 65, 0.12)',
          height: 1,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 32,
          fontFamily: 'Red Hat Display',
          fontWeight: 700,
          letterSpacing: 1.25,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&[data-testid="header-action-menu"]': {
            color: `${theme.palette.primary.main} !important`,
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          overflowWrap: 'break-word',
        },
      },
    },
  },
});
